import { boot, Intercom, shutdown, update } from '@intercom/messenger-js-sdk';
import type { SessionUser } from '@/api/profile/types';
import { isIntercomLauncherDismissed } from './intercomLauncherState';

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID ?? '';

/**
 * Launcher offsets, sent on every boot/update to override the workspace's own
 * `launcher_settings`.
 *
 * The production workspace ships `vertical_padding: 2000`, which parks the
 * launcher — and the messenger panel with it — 2000px above the bottom edge,
 * off-screen. That is deliberate for the old dashboard, which hides the default
 * bubble and drives its own `.intercom-launcher` button instead. This app uses
 * the default launcher, so it overrides the offset rather than inheriting it.
 * 20px matches `IntercomLauncherDismiss`'s position.
 */
const LAUNCHER_PADDING = { vertical_padding: 20, horizontal_padding: 20 };

// Mirrors the old frontend's cookie-consent gate (frontend/src/lib/intercom.tsx
// + CustomIntercomLauncher.jsx) — Intercom sets its own cookies, so nothing
// boots until the user explicitly accepts. Key name kept identical for
// consistency, though it's a separate origin's localStorage.
//
// Three states, so a decliner is never re-prompted:
//   - key absent  → UNDECIDED  (show the consent prompt)
//   - 'true'      → ACCEPTED   (booted + identified)
//   - 'false'     → DECLINED   (decided, never booted, never re-prompted)
const CONSENT_KEY = 'allowedIntercomCookies';
const CONSENT_DECLINED = 'false';

// Consent is decided from two surfaces that write this one key: the app-level
// banner and the "?" help dialog. A persistently-mounted surface (the banner)
// must react when the *other* one decides, rather than reading consent once —
// otherwise granting consent via "?" leaves a stale banner up, and declining on
// that stale banner would silently revoke the consent just granted. So both
// writers notify these listeners; the banner subscribes.
const consentListeners = new Set<() => void>();

export function subscribeIntercomConsent(onChange: () => void): () => void {
  consentListeners.add(onChange);
  return () => {
    consentListeners.delete(onChange);
  };
}

function notifyConsentChange(): void {
  for (const notify of consentListeners) notify();
}

export function hasIntercomConsent(): boolean {
  return localStorage.getItem(CONSENT_KEY) === 'true';
}

/** Whether an Intercom workspace is configured for this build. When it is not
 *  (E2E, or any env without `VITE_INTERCOM_APP_ID`), there is no Intercom to
 *  consent to — so the consent prompt must not be shown. Read from the env
 *  lazily so tests can stub it. Mirrors the `!INTERCOM_APP_ID` guard every other
 *  function in this file already applies. */
export function isIntercomConfigured(): boolean {
  return Boolean(import.meta.env.VITE_INTERCOM_APP_ID);
}

/** Whether the user has made a cookie-consent choice at all — accept OR
 *  decline. Drives the app-level prompt: an undecided user is asked, a decided
 *  one (either way) is left alone. A storage failure counts as "decided" so a
 *  browser that cannot persist the choice never shows an undismissable prompt. */
export function hasDecidedIntercomConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) !== null;
  } catch {
    return true;
  }
}

/** Records a decline: the user is decided, so nothing boots and the prompt
 *  never returns. The counterpart to `acceptIntercomConsent`. */
export function declineIntercomConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, CONSENT_DECLINED);
  } catch {
    // Private mode / storage disabled — nothing to persist. The prompt is
    // hidden for this session regardless (its own state drives that).
  }
  notifyConsentChange();
}

function bootWidget(): void {
  // No `custom_launcher_selector` — that tells Intercom to bind its own click
  // listener to matching elements, which hijacks the click before our Help
  // button's onClick runs. We open the widget ourselves via `showIntercomChat`.
  //
  // Boot with the user's own preference rather than a hardcoded `false`.
  //
  // Intercom also remembers this flag per browser, but its copy is not the
  // source of truth — ours is, so it is passed explicitly either way. Hardcoding
  // `false` meant a dismissed launcher came back on every reload: boot showed
  // it, and the effect that re-hides it raced a widget that had not finished
  // booting, so the update was dropped.
  const hidden = isIntercomLauncherDismissed();
  Intercom({
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: hidden,
    ...LAUNCHER_PADDING,
  });
  boot({
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: hidden,
    ...LAUNCHER_PADDING,
  });
  // `useIntercomLauncherDismiss`'s effect runs while the widget may still be
  // booting, and an `update` sent that early is dropped — leaving the launcher
  // stuck at its boot-time value. Re-assert once the script has loaded.
  document
    .getElementById('_intercom_npm_loader')
    ?.addEventListener('load', () => {
      window.Intercom?.('update', {
        hide_default_launcher: isIntercomLauncherDismissed(),
        ...LAUNCHER_PADDING,
      });
    });
}

/**
 * Show or hide the floating launcher at runtime.
 *
 * The bubble is pinned bottom-right, where it can cover a sheet's footer (the
 * Importer's "Import" button, for one). Left placement is no escape: the
 * sidebar's collapse chevron owns the bottom-left corner.
 *
 * It is *not* hidden automatically. Support stays reachable while a sheet is
 * open, so the launcher floats above sheets by design — see the overlay
 * stacking block in `globals.css` — and the user closes it by hand when it is
 * in the way. Only two things call this: `useIntercomLauncherDismiss` (the ✕)
 * and the header's Help button, which brings it back.
 */
export function setIntercomLauncherVisible(visible: boolean): void {
  if (!INTERCOM_APP_ID || !hasIntercomConsent()) return;
  window.Intercom?.('update', {
    hide_default_launcher: !visible,
    ...LAUNCHER_PADDING,
  });
}

/** Boots Intercom if consent was already granted in an earlier session. Call
 *  once at app startup — no-op without an app id or without consent. */
export function initIntercom(): void {
  if (!INTERCOM_APP_ID || !hasIntercomConsent()) return;
  bootWidget();
}

/** Identifies the current user to Intercom. No-op without consent — safe to
 *  call on every session change. Carries the fields the old frontend sent, so
 *  the same person does not arrive as two different contacts depending on which
 *  dashboard they accepted from; fields absent from the payload are omitted
 *  rather than sent empty. One v1 field is still missing: the company's
 *  `created_at`, which `SessionUser['company']` does not carry.
 *
 *  While impersonating, identifies the OPERATOR (`unImpersonatedUser`), never
 *  the impersonated customer — matching the old dashboard
 *  (`CustomIntercomLauncher.jsx`: `me.unImpersonatedUser ?? me`). The human at
 *  the keyboard is the operator; the customer never consented, and their
 *  identity must not reach Intercom in the operator's browser. v2's
 *  `unImpersonatedUser` is lean (id + company), so the operator identify carries
 *  just those — enough to attribute the session to the right person. */
export function identifyIntercomUser(user: SessionUser | null): void {
  if (!INTERCOM_APP_ID || !user || !hasIntercomConsent()) return;

  const operator = user.unImpersonatedUser;
  if (operator?.id) {
    if (!operator.company?.id) return;
    update({
      // Carried on every `update` — see `pageChangeIntercom`: an update is a
      // full re-read, so omitting these lets the workspace's own
      // `launcher_settings` park the launcher off-screen and resurrect a
      // dismissed one.
      hide_default_launcher: isIntercomLauncherDismissed(),
      ...LAUNCHER_PADDING,
      user_id: operator.id,
      company: {
        company_id: operator.company.id,
        ...(operator.company.name ? { name: operator.company.name } : {}),
      },
    });
    return;
  }

  if (!user.company?.id) return;
  update({
    // As above: every `update` re-reads the workspace settings.
    hide_default_launcher: isIntercomLauncherDismissed(),
    ...LAUNCHER_PADDING,
    user_id: user.id,
    // Identity Verification: without this, Intercom treats the user_id as
    // unauthenticated (and rejects it when the workspace enforces IV). v1 always
    // sent it; v2 had dropped it.
    ...(user.intercomHash ? { user_hash: user.intercomHash } : {}),
    email: user.email,
    name: `${user.firstName} ${user.lastName}`,
    ...(user.createdAt
      ? { created_at: new Date(user.createdAt).getTime() / 1000 }
      : {}),
    ...(user.avatarFileUrl
      ? {
          avatar: {
            type: 'avatar',
            image_url: `${user.avatarFileUrl}?fit=crop&h=256&w=256`,
          },
        }
      : {}),
    company: {
      company_id: user.company.id,
      // `officialName` (the registered legal name), not `name` (the display
      // name) — v1 sent `officialName`, and sending the other one here would
      // rename the company in Intercom depending on which frontend the user
      // last accepted from. `||`, not `??`: the me-response schema permits an
      // empty string, and an empty company name is worse than the display one.
      name: user.company.officialName || user.company.name,
      ...(user.company.website ? { website: user.company.website } : {}),
      // Segmentation fields v1 sent and v2 had dropped.
      ...(user.company.isClientCompany != null
        ? { is_client: Boolean(user.company.isClientCompany) }
        : {}),
      ...(user.company.facilityProcessNames
        ? { facility_processes: user.company.facilityProcessNames }
        : {}),
    },
  });
}

/** Grants consent, boots the widget, and identifies the user — call from the
 *  consent dialog's Accept action. */
export function acceptIntercomConsent(user: SessionUser | null): void {
  try {
    localStorage.setItem(CONSENT_KEY, 'true');
  } catch {
    // Private mode / storage disabled — can't persist across reloads, but still
    // boot for this session so Accept isn't a dead click. Mirrors the guard in
    // `declineIntercomConsent`.
  }
  bootWidget();
  identifyIntercomUser(user);
  notifyConsentChange();
}

/** Opens the chat widget. Only meaningful after `initIntercom`/
 *  `acceptIntercomConsent` has booted it. */
export function showIntercomChat(): void {
  window.Intercom?.('show');
}

/** Pings Intercom on navigation so it refreshes "last seen" and can surface any
 *  URL-targeted messages — call on every route change. No-op without consent
 *  (nothing is booted). Mirrors the old dashboard's per-navigation `update`
 *  (`authorized-page.jsx` → `intercom.tsx` `pageChange`), which v2 had dropped.
 *
 *  Both settings ride along, because an `update` is a full re-read rather than
 *  a patch — a bare `update({})` fell back to the workspace's own
 *  `launcher_settings` and undid them:
 *
 *  - `LAUNCHER_PADDING`, or the workspace's `vertical_padding: 2000` parks the
 *    launcher off-screen. Any route change did it, and opening a dialog is one
 *    — `?dialog=import-styles` made the bubble vanish on the way in.
 *  - `hide_default_launcher`, or a launcher the user dismissed comes back on
 *    the next navigation, while the ✕ stays gone (our flag still reads
 *    dismissed) — leaving a bubble with no way to close it. */
export function pageChangeIntercom(): void {
  if (!INTERCOM_APP_ID || !hasIntercomConsent()) return;
  update({
    hide_default_launcher: isIntercomLauncherDismissed(),
    ...LAUNCHER_PADDING,
  });
}

/** Tears down the Intercom session on logout/end-impersonation. No-op
 *  without consent (nothing was ever booted). */
export function shutdownIntercom(): void {
  if (!INTERCOM_APP_ID || !hasIntercomConsent()) return;
  shutdown();
}
